import React, { useMemo, useState } from "react";

/**
 * EUAutoAdjustRules
 * - Whole-number % inputs (5 => 5)
 * - Subject never adjusted (parent enforces)
 * - QUALITY: no live syncing; click "Refresh from grid" to pull distinct qualities and merge them.
 */
export default function EUAutoAdjustRules({
  rules,
  onChange,     // (nextRules) => void
  unitLabel,    // "SF" or "Unit" (for UI hint)
  onApply,      // () => void
  qualities = [], // distinct strings from the grid; NOT auto-applied until refresh
}) {
  const defaults = defaultRules();
  const r = useMemo(() => mergeRules(rules, defaults), [rules]);

  function set(path, val) {
    const next = mergeRules(r, defaults);
    let node = next;
    const parts = path.split(".");
    for (let i = 0; i < parts.length - 1; i++) {
      node[parts[i]] = node[parts[i]] || {};
      node = node[parts[i]];
    }
    node[parts[parts.length - 1]] = val;
    onChange?.(next);
  }

  // --- Manual refresh for qualities (no live effect) ---
  function refreshQualitiesFromGrid() {
    const clean = (Array.isArray(qualities) ? qualities : [])
      .map((q) => (q == null ? "" : String(q)))
      .filter((q) => q.trim() !== "");
    const uniq = [];
    for (const q of clean) {
      if (!uniq.some((x) => sameQ(x, q))) uniq.push(q);
    }
    const current = Array.isArray(r.quality.order) ? r.quality.order.filter(Boolean) : [];
    const kept = current.filter((c) => uniq.some((q) => sameQ(q, c)));
    const added = uniq.filter((q) => !kept.some((c) => sameQ(c, q)));
    set("quality.order", [...kept, ...added]);
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            Equal &amp; Uniform
          </div>
          <div className="text-base font-semibold text-slate-900">
            Auto Adjustments
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] text-slate-600">
          <span className="font-medium text-slate-700">Tip:</span>
          <span>Subject is never adjusted</span>
        </div>
      </div>

      {/* Size rule */}
      <RuleCard
        title="Size"
        subtitle="Economies of scale"
        enabled={!!r.size.enabled}
        onToggle={(checked) => set("size.enabled", checked)}
        accent="sky"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Field
            label="% per difference"
            helper="Whole % (5 = 5%)"
          >
            <input
              inputMode="decimal"
              value={r.size.pctPer ?? ""}
              onChange={(e) => set("size.pctPer", sanitizePctInt(e.target.value))}
              placeholder="e.g., 5"
              className={inputClass()}
            />
          </Field>

          <Field
            label="per"
            helper={unitLabel}
          >
            <input
              inputMode="numeric"
              value={r.size.amountPer ?? ""}
              onChange={(e) => set("size.amountPer", sanitizeNumber(e.target.value))}
              placeholder={unitLabel === "SF" ? "10,000" : "10"}
              className={inputClass()}
            />
          </Field>

          <Field label="Larger comps adjust">
            <select
              value={r.size.largerDirection || "up"}
              onChange={(e) => set("size.largerDirection", e.target.value)}
              className={inputClass()}
            >
              <option value="up">Up</option>
              <option value="down">Down</option>
            </select>
          </Field>

          <Field label="Smaller comps adjust">
            <select
              value={r.size.smallerDirection || "down"}
              onChange={(e) => set("size.smallerDirection", e.target.value)}
              className={inputClass()}
            >
              <option value="up">Up</option>
              <option value="down">Down</option>
            </select>
          </Field>
        </div>
      </RuleCard>

      {/* Age rule */}
      <RuleCard
        title="Age"
        subtitle="Year built"
        enabled={!!r.age.enabled}
        onToggle={(checked) => set("age.enabled", checked)}
        accent="amber"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Field
            label="% per"
            helper="Whole % (2 = 2%)"
          >
            <input
              inputMode="decimal"
              value={r.age.pctPer ?? ""}
              onChange={(e) => set("age.pctPer", sanitizePctInt(e.target.value))}
              placeholder="e.g., 2"
              className={inputClass()}
            />
          </Field>

          <Field label="years of diff">
            <input
              inputMode="numeric"
              value={r.age.yearsPer ?? ""}
              onChange={(e) => set("age.yearsPer", sanitizeNumber(e.target.value))}
              placeholder="5"
              className={inputClass()}
            />
          </Field>

          <Field label="Older comps adjust">
            <select
              value={r.age.olderDirection || "up"}
              onChange={(e) => set("age.olderDirection", e.target.value)}
              className={inputClass()}
            >
              <option value="up">Up</option>
              <option value="down">Down</option>
            </select>
          </Field>

          <Field label="Newer comps adjust">
            <select
              value={r.age.newerDirection || "down"}
              onChange={(e) => set("age.newerDirection", e.target.value)}
              className={inputClass()}
            >
              <option value="up">Up</option>
              <option value="down">Down</option>
            </select>
          </Field>
        </div>
      </RuleCard>

      {/* Quality rule */}
      <RuleCard
        title="Quality"
        subtitle="Best → worst"
        enabled={!!r.quality.enabled}
        onToggle={(checked) => set("quality.enabled", checked)}
        accent="violet"
        headerRight={
          <button
            type="button"
            onClick={refreshQualitiesFromGrid}
            className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:border-slate-400 hover:bg-slate-50"
            title="Pull distinct quality values from the grid and merge into this list"
          >
            Refresh from grid
          </button>
        }
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
          <div className="md:col-span-6">
            <Field label="Order qualities (drag to sort)">
              <div className="min-h-[44px] rounded-xl border border-slate-200 bg-white px-3 py-3">
                <ReorderPills
                  items={Array.isArray(r.quality.order) ? r.quality.order.filter(Boolean) : []}
                  onReorder={(next) => set("quality.order", next)}
                />
              </div>
            </Field>
          </div>

          <div className="md:col-span-2">
            <Field
              label="% per step"
              helper="Whole % (3 = 3%/step)"
            >
              <input
                inputMode="decimal"
                value={r.quality.pctPerStep ?? ""}
                onChange={(e) => set("quality.pctPerStep", sanitizePctInt(e.target.value))}
                placeholder="e.g., 3"
                className={inputClass()}
              />
            </Field>
          </div>

          <div className="md:col-span-2">
            <Field label="Superior comp adjusts">
              <select
                value={r.quality.superiorDirection || "down"}
                onChange={(e) => set("quality.superiorDirection", e.target.value)}
                className={inputClass()}
              >
                <option value="up">Up</option>
                <option value="down">Down</option>
              </select>
            </Field>
          </div>

          <div className="md:col-span-2">
            <Field label="Inferior comp adjusts">
              <select
                value={r.quality.inferiorDirection || "up"}
                onChange={(e) => set("quality.inferiorDirection", e.target.value)}
                className={inputClass()}
              >
                <option value="up">Up</option>
                <option value="down">Down</option>
              </select>
            </Field>
          </div>
        </div>
      </RuleCard>

      {/* Land / Building Ratio rule */}
      <RuleCard
        title="Land / Building Ratio"
        subtitle="Land SF per building size"
        enabled={!!r.landRatio.enabled}
        onToggle={(checked) => set("landRatio.enabled", checked)}
        accent="sky"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Field
            label="% per difference"
            helper="Whole % (3 = 3%)"
          >
            <input
              inputMode="decimal"
              value={r.landRatio.pctPer ?? ""}
              onChange={(e) => set("landRatio.pctPer", sanitizePctInt(e.target.value))}
              placeholder="e.g., 3"
              className={inputClass()}
            />
          </Field>

          <Field
            label="per ratio step"
            helper={`Land SF / Building ${unitLabel}`}
          >
            <input
              inputMode="decimal"
              value={r.landRatio.ratioPer ?? ""}
              onChange={(e) => set("landRatio.ratioPer", sanitizeNumber(e.target.value))}
              placeholder="e.g., 0.5"
              className={inputClass()}
            />
          </Field>

          <Field label="Higher ratio comps adjust">
            <select
              value={r.landRatio.higherDirection || "down"}
              onChange={(e) => set("landRatio.higherDirection", e.target.value)}
              className={inputClass()}
            >
              <option value="up">Up</option>
              <option value="down">Down</option>
            </select>
          </Field>

          <Field label="Lower ratio comps adjust">
            <select
              value={r.landRatio.lowerDirection || "up"}
              onChange={(e) => set("landRatio.lowerDirection", e.target.value)}
              className={inputClass()}
            >
              <option value="up">Up</option>
              <option value="down">Down</option>
            </select>
          </Field>
        </div>
      </RuleCard>

      <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-slate-600">
          Percent entries are whole numbers (e.g., <b>5</b> = 5%).
        </div>

        <button
          type="button"
          onClick={onApply}
          className="inline-flex items-center justify-center rounded-xl border border-sky-200 bg-sky-50 px-4 py-2.5 text-sm font-medium text-sky-700 transition hover:border-sky-300 hover:bg-sky-100"
          title="Apply auto rules now"
        >
          Apply to comps
        </button>
      </div>
    </div>
  );
}

/* ------------------- mini components & utils ------------------- */

function inputClass() {
  return "h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100";
}

function Field({ label, helper, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium tracking-wide text-slate-600">
        {label}
      </label>
      {children}
      {helper ? (
        <div className="min-h-[16px] text-[11px] text-slate-500">{helper}</div>
      ) : (
        <div className="min-h-[16px]" />
      )}
    </div>
  );
}

function RuleCard({
  title,
  subtitle,
  enabled,
  onToggle,
  accent = "sky",
  headerRight = null,
  children,
}) {
  const accents = {
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={!!enabled}
            onChange={(e) => onToggle?.(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
          />

          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            {subtitle ? (
              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${accents[accent] || accents.sky}`}>
                {subtitle}
              </span>
            ) : null}
          </div>
        </div>

        {headerRight ? <div>{headerRight}</div> : null}
      </div>

      {enabled ? (
        <div className="mt-4">
          {children}
        </div>
      ) : null}
    </div>
  );
}

function sameQ(a, b) {
  const s = (x) => String(x ?? "").trim().toLowerCase();
  return s(a) === s(b);
}

function ReorderPills({ items, onReorder }) {
  const [dragIdx, setDragIdx] = useState(null);

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it, idx) => (
        <span
          key={`${String(it)}-${idx}`}
          draggable
          onDragStart={() => setDragIdx(idx)}
          onDragOver={(e) => {
            e.preventDefault();
            if (dragIdx == null || dragIdx === idx) return;
            const next = items.slice();
            const [m] = next.splice(dragIdx, 1);
            next.splice(idx, 0, m);
            setDragIdx(idx);
            onReorder?.(next);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragIdx(null);
          }}
          className="select-none cursor-grab rounded-full border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:border-slate-400"
          title="Drag to reorder (top = best)"
        >
          {String(it || "").trim() || "(blank)"}
        </span>
      ))}
      {items.length === 0 && (
        <span className="text-xs text-slate-500">No qualities yet — click Refresh.</span>
      )}
    </div>
  );
}

export function defaultRules() {
  return {
    size: {
      enabled: true,
      pctPer: 1,               // 1% per difference
      amountPer: 5000,         // per 5,000 SF
      largerDirection: "up",
      smallerDirection: "down",
    },
    age: {
      enabled: true,
      pctPer: 1,               // 1% per
      yearsPer: 5,             // 5 years of diff
      olderDirection: "up",
      newerDirection: "down",
    },
    quality: {
      enabled: false,
      pctPerStep: 5,
      superiorDirection: "down",
      inferiorDirection: "up",
      order: [],
    },
    landRatio: {
      enabled: true,
      pctPer: 1,               // 1% per difference
      ratioPer: 0.5,           // per 0.5 ratio step
      higherDirection: "down",
      lowerDirection: "up",
    },
  };
}

function mergeRules(r, d) {
  const out = { ...d, ...(r || {}) };
  out.size = { ...d.size, ...(r?.size || {}) };
  out.age = { ...d.age, ...(r?.age || {}) };
  out.quality = { ...d.quality, ...(r?.quality || {}) };
  out.landRatio = { ...d.landRatio, ...(r?.landRatio || {}) };
  return out;
}

function sanitizeNumber(x) {
  const s = String(x ?? "").replace(/[^\d.-]/g, "");
  if (!s) return "";
  if (s === "." || s === "-" || s === "-.") return s;
  const n = Number(s);
  return Number.isFinite(n) ? s : "";
}

export function sanitizePctInt(x) {
  const raw = String(x ?? "").replace(/%/g, "");
  let s = raw.replace(/[^\d\-.]/g, "");
  s = s.replace(/(?!^)-/g, "");
  s = s.replace(/\.(?=.*\.)/g, "");

  if (s === "" || s === "-" || s === "." || s === "-.") return s;

  const m = s.match(/^(-?\d+)(?:\.(\d{0,2}))?$/);
  if (!m) {
    const m2 = s.match(/^(-?\d+)\.(\d+)/);
    if (m2) return `${m2[1]}.${m2[2].slice(0, 2)}`;
    const n = Number(s);
    if (Number.isFinite(n)) return String(Math.round(n * 100) / 100);
    return "";
  }
  return m[2] === undefined ? m[1] : `${m[1]}.${m[2]}`;
}